import Component from '@glimmer/component';
import { LinkTo } from '@ember/routing';
import { eq } from 'ember-truth-helpers';
import { formatDate } from '../../helpers/format-date';
import MatchScore from './match/match-score';

// Composable, data-driven match list (HLTV stacked rows + Liquipedia Matchlist).
// Groups matches by status (Live → Upcoming → Final) and then by event, tags
// each row with its real Bo format, and floats live matches to the top. Reused
// across tournament and match pages instead of bespoke per-event markup.
//
// Args:
//   @matches - array of Match objects (teams, bestOf, isLive/isFinal,
//              scheduledDate, rounds, tournament). Rows with < 2 teams are
//              skipped so we never render a half-empty fixture.
//   @heading - optional section title.
const STATUS_LABEL = { live: 'Live', upcoming: 'Upcoming', final: 'Final' };
const STATUS_ORDER = ['live', 'upcoming', 'final'];

export default class MatchList extends Component {
  get groups() {
    const matches = (this.args.matches ?? []).filter(
      (m) => m && Array.isArray(m.teams) && m.teams.length >= 2,
    );

    // status -> Map(eventName -> rows[])
    const byStatus = { live: new Map(), upcoming: new Map(), final: new Map() };
    for (const m of matches) {
      const status = m.isLive ? 'live' : m.isFinal ? 'final' : 'upcoming';
      const eventName = m.tournament?.name ?? m.tournament?.title ?? 'Matches';
      const row = {
        match: m,
        teamA: m.teams[0],
        teamB: m.teams[1],
        decided: m.isLive || m.isFinal,
      };
      const bucket = byStatus[status];
      if (!bucket.has(eventName)) bucket.set(eventName, []);
      bucket.get(eventName).push(row);
    }

    const groups = [];
    for (const status of STATUS_ORDER) {
      const events = [];
      for (const [name, rows] of byStatus[status]) {
        events.push({ name, rows });
      }
      if (events.length) {
        groups.push({
          status,
          label: STATUS_LABEL[status],
          events,
          multiEvent: events.length > 1,
        });
      }
    }
    return groups;
  }

  <template>
    {{#if this.groups.length}}
      <div class='esports-match-list widget-item'>
        {{#if @heading}}
          <div class='section-title-wrap violet small-space'>
            <h2 class='section-title medium'>{{@heading}}</h2>
            <div class='section-title-separator'></div>
          </div>
        {{/if}}

        {{#each this.groups as |group|}}
          <div class='eml-status-group eml-status-group--{{group.status}}'>
            <div class='eml-status-head'>
              {{#if (eq group.status 'live')}}
                <span class='eml-live-dot' aria-hidden='true'></span>
              {{/if}}
              <span class='eml-status-label'>{{group.label}}</span>
            </div>

            {{#each group.events as |event|}}
              <div class='eml-event'>
                {{#if group.multiEvent}}
                  <p class='eml-event-name'>{{event.name}}</p>
                {{/if}}
                <ul class='eml-rows'>
                  {{#each event.rows as |row|}}
                    <li class='eml-row'>
                      <LinkTo
                        @route='esports.matches.match'
                        @model={{row.match.slug}}
                        class='eml-link'
                      >
                        {{#if row.match.bestOf}}
                          <span class='eml-format'>Bo{{row.match.bestOf}}</span>
                        {{/if}}
                        <span class='eml-team'>
                          <img class='eml-logo' src={{row.teamA.logo.url}} alt='' loading='lazy' />
                          <span class='eml-name'>{{row.teamA.name}}</span>
                        </span>
                        <span class='eml-mid'>
                          {{#if row.decided}}
                            <MatchScore
                              @teamA={{row.teamA}}
                              @teamB={{row.teamB}}
                              @rounds={{row.match.rounds}}
                            />
                          {{else}}
                            <span class='eml-vs'>vs</span>
                          {{/if}}
                        </span>
                        <span class='eml-team eml-team--right'>
                          <span class='eml-name'>{{row.teamB.name}}</span>
                          <img class='eml-logo' src={{row.teamB.logo.url}} alt='' loading='lazy' />
                        </span>
                        <span class='eml-when'>
                          {{#if row.match.isLive}}
                            <span class='eml-when-live'>LIVE</span>
                          {{else if row.match.scheduledDate}}
                            {{formatDate row.match.scheduledDate 'MMM DD'}}
                          {{else if row.match.isFinal}}
                            Final
                          {{else}}
                            TBD
                          {{/if}}
                        </span>
                      </LinkTo>
                    </li>
                  {{/each}}
                </ul>
              </div>
            {{/each}}
          </div>
        {{/each}}
      </div>
    {{/if}}
  </template>
}
